// Uhren: Übersicht (Spalten mit farbigen Elementzeilen + Torte), Editor mit
// drei Bereichen (Elementtypen · Ablauf · Eigenschaften), Einfach/Erweitert, Testlauf.

import { useEffect, useState } from "react";
import { api, fmtMs, toast, useLoad } from "../../api";
import { Empty, Field, Modal, Switch, Tip, useMe } from "../../ui";

export const ELEMENT_TYPES: { type: string; label: string; icon: string; color: string; song: boolean }[] = [
  { type: "music", label: "Musikkategorie", icon: "♪", color: "#2f6fb1", song: true },
  { type: "category_group", label: "Kategoriegruppe", icon: "♫", color: "#3d5a99", song: true },
  { type: "title", label: "Bestimmter Titel", icon: "◉", color: "#1f8a8a", song: true },
  { type: "jingle", label: "Jingle", icon: "✦", color: "#e8673c", song: true },
  { type: "sweeper", label: "Sweeper", icon: "≈", color: "#c98a12", song: true },
  { type: "promo", label: "Promo", icon: "★", color: "#a35d2a", song: true },
  { type: "ad_break", label: "Werbeblock", icon: "€", color: "#c8382f", song: false },
  { type: "news", label: "Nachrichten", icon: "N", color: "#5b6b2f", song: false },
  { type: "weather", label: "Wetter", icon: "☀", color: "#6b7f2a", song: false },
  { type: "time_announce", label: "Zeitansage", icon: "◷", color: "#7c8a84", song: false },
  { type: "top_of_hour", label: "Top-of-Hour-ID", icon: "⏱", color: "#b0457a", song: false },
  { type: "voicetrack", label: "Voicetrack-Slot", icon: "🎙", color: "#7a4fb3", song: false },
  { type: "program", label: "Programm", icon: "▤", color: "#4a5a53", song: false },
  { type: "sponsor", label: "Sponsor-Lead-in", icon: "§", color: "#a35d2a", song: false },
  { type: "live", label: "Live-Marker", icon: "●", color: "#d6334a", song: false },
  { type: "network", label: "Netz-Marker", icon: "⇄", color: "#3d5a99", song: false },
  { type: "relay", label: "Simulcast/Relay", icon: "⇶", color: "#1f8a8a", song: false },
  { type: "block", label: "Block", icon: "▢", color: "#7c8a84", song: false },
];
export const typeInfo = (t: string) => ELEMENT_TYPES.find((x) => x.type === t) ?? ELEMENT_TYPES[0];
const DAYS = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

export const ROLE_LABEL: Record<string, string> = { dropper: "Dropper", jingle: "Jingle", sweeper: "Sweeper", id: "Station-ID", promo: "Promo", ad: "Spot" };

function elColor(e: any) {
  return e.category_color ?? typeInfo(e.type).color;
}
function elLabel(e: any) {
  if (e.label) return e.label;
  if (e.role) return `${ROLE_LABEL[e.role] ?? e.role} (je Sender)`;
  if (e.pool_name) return `${typeInfo(e.type).label}: ${e.pool_name}`;
  if (e.news_name) return `Nachrichten: ${e.news_name}`;
  if (e.type === "music") return e.category_name ?? "Musik (Kategorie wählen)";
  if (e.type === "category_group") return e.group_name ?? "Gruppe wählen";
  if (e.type === "title") return e.media_title ?? "Titel wählen";
  return typeInfo(e.type).label;
}
const flat = (els: any[]): any[] => els.flatMap((e) => (e.type === "block" ? flat(e.children ?? []) : [e]));

/** Pie of the hour: share of each element by its estimated length. */
function Pie({ clock, size = 120 }: { clock: any; size?: number }) {
  const els = flat(clock.elements ?? []);
  const len = (e: any) => e.duration_ms ?? (typeInfo(e.type).song ? 210_000 : 60_000);
  const total = Math.max(3_600_000, els.reduce((a, e) => a + len(e), 0));
  let acc = 0;
  const r = size / 2;
  const arc = (from: number, to: number) => {
    const a0 = (from / total) * 2 * Math.PI - Math.PI / 2, a1 = (to / total) * 2 * Math.PI - Math.PI / 2;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    return `M${r},${r} L${r + r * Math.cos(a0)},${r + r * Math.sin(a0)} A${r},${r} 0 ${large} 1 ${r + r * Math.cos(a1)},${r + r * Math.sin(a1)} Z`;
  };
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={r} cy={r} r={r} fill="#f1eee6" />
      {els.map((e, i) => {
        const from = acc;
        acc += len(e);
        return <path key={i} d={arc(from, Math.min(acc, total - 1))} fill={elColor(e)} stroke="#fff" strokeWidth={1}><title>{elLabel(e)}</title></path>;
      })}
      <circle cx={r} cy={r} r={r * 0.45} fill="#fff" />
    </svg>
  );
}

function newElement(type: string): any {
  return { key: Math.random().toString(36).slice(2), type, label: "", timing: "none", select_mode: "rotate", energy_strength: 0, slot_rules: {}, children: [] };
}

function ElementProps({ el, set, advanced, cats, groups, pools, news, weather }: { el: any; set: (p: any) => void; advanced: boolean; cats: any[]; groups: any[]; pools: any[]; news: any[]; weather: any[] }) {
  // Roles are defined freely (Bibliothek › Rollen-Zuordnung), e.g. "Stundenopener".
  const { data: roleDefs } = useLoad<any[]>("/api/role-defs");
  for (const r of roleDefs ?? []) ROLE_LABEL[r.key] = r.label;
  const [songQ, setSongQ] = useState("");
  const [songs, setSongs] = useState<any[]>([]);
  useEffect(() => {
    if (el.type !== "title" || songQ.length < 2) return;
    const t = window.setTimeout(async () => setSongs((await api(`/api/library?q=${encodeURIComponent(songQ)}`)).items.slice(0, 8)), 250);
    return () => window.clearTimeout(t);
  }, [songQ, el.type]);
  const info = typeInfo(el.type);
  const msField = (k: string, label: string, hint?: string) => (
    <Field label={label} hint={hint}>
      <input type="number" min={0} step={1} value={el[k] != null ? Math.round(el[k] / 1000) : ""} onChange={(e) => set({ [k]: e.target.value === "" ? null : Number(e.target.value) * 1000 })} />
    </Field>
  );
  const atMin = el.at_ms != null ? Math.floor(el.at_ms / 60000) : "";
  const atSec = el.at_ms != null ? Math.round((el.at_ms % 60000) / 1000) : "";
  return (
    <div className="form">
      <Field label="Typ" full><b>{info.icon} {info.label}</b></Field>
      <Field label="Bezeichnung" full hint="optional, sonst Name der Kategorie bzw. des Typs"><input type="text" value={el.label ?? ""} onChange={(e) => set({ label: e.target.value })} /></Field>
      {["jingle", "sweeper", "promo", "top_of_hour", "sponsor"].includes(el.type) && (
        <Field label="Asset-Pool" full hint="optional – oder unten einfach eine Kategorie wählen (z. B. Dropper)">
          <select value={el.pool_id ?? ""} onChange={(e) => set({ pool_id: e.target.value || null })}>
            <option value="">– kein Pool –</option>{pools.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.clips})</option>)}
          </select>
        </Field>
      )}
      {el.type === "news" && (
        <Field label="Nachrichtenquelle" full>
          <select value={el.news_source_id ?? ""} onChange={(e) => set({ news_source_id: e.target.value || null })}>
            <option value="">– wählen –</option>{news.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
          </select>
        </Field>
      )}
      {el.type === "weather" && (
        <Field label="Wetterquelle" full>
          <select value={el.news_source_id ?? ""} onChange={(e) => set({ news_source_id: e.target.value || null })}>
            <option value="">– wählen –</option>{weather.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
          </select>
        </Field>
      )}
      {["music", "jingle", "sweeper", "promo", "top_of_hour", "station_id", "sponsor"].includes(el.type) && !el.pool_id && (
        <Field label="Je Sender (Rolle)" full hint="Für Dropper, Jingles, Sweeper, IDs oder eigene Rollen wie Stundenopener: jeder Sender spielt hier seinen eigenen Ordner dieser Rolle – die Uhr bleibt für alle gleich. Rollen anlegen: Bibliothek › Rollen-Zuordnung.">
          <select value={el.role ?? ""} onChange={(e) => set({ role: e.target.value || null, ...(e.target.value ? { category_id: null } : {}) })}>
            <option value="">– feste Kategorie (unten) –</option>
            {(roleDefs ?? Object.entries(ROLE_LABEL).map(([key, label]) => ({ key, label }))).map((r: any) => <option key={r.key} value={r.key}>{r.label} des jeweiligen Senders</option>)}
          </select>
        </Field>
      )}
      {!el.role && (el.type === "music" || (["jingle", "sweeper", "promo", "top_of_hour", "station_id", "sponsor"].includes(el.type) && !el.pool_id)) && (
        <Field label="Kategorie" full>
          <select value={el.category_id ?? ""} onChange={(e) => set({ category_id: e.target.value || null })}>
            <option value="">– wählen –</option>
            {[...cats].sort((a, b) => (el.type === "music" ? Number(a.kind !== "music") - Number(b.kind !== "music") : 0)).map((c) => <option key={c.id} value={c.id}>{c.name} ({c.songs}){c.kind !== "music" ? " · Jingle/Imaging" : ""}</option>)}
          </select>
        </Field>
      )}
      {el.type === "category_group" && (
        <Field label="Kategoriegruppe" full>
          <select value={el.group_id ?? ""} onChange={(e) => set({ group_id: e.target.value || null })}>
            <option value="">– wählen –</option>{groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </Field>
      )}
      {el.type === "title" && (
        <Field label="Titel" full hint={el.media_title ? `gewählt: ${el.media_title}` : "Suche nach Titel oder Interpret"}>
          <input type="text" value={songQ} onChange={(e) => setSongQ(e.target.value)} />
          {songs.map((s) => <div key={s.id} className="small" style={{ cursor: "pointer", padding: 2 }} onClick={() => { set({ media_id: s.id, media_title: `${s.artist} – ${s.title}` }); setSongs([]); setSongQ(""); }}>{s.artist} – {s.title}</div>)}
        </Field>
      )}
      {!info.song && el.type !== "block" && msField("duration_ms", "Geplante Länge (s)", "für Werbeblock, Nachrichten usw.")}
      {el.type === "relay" && <Field label="Relay-Adresse" full><input type="url" value={el.relay_url ?? ""} onChange={(e) => set({ relay_url: e.target.value })} /></Field>}
      {(advanced || el.type === "block") && (
        <>
          <Field label="Zeit" hint="feste Position in der Stunde">
            <select value={el.timing ?? "none"} onChange={(e) => set({ timing: e.target.value })}>
              <option value="none">keine (fließt)</option><option value="hard">hart (genau zur Zeit)</option><option value="soft">weich (nicht vorher)</option><option value="backtimed">rückwärts (endet zur Zeit)</option>
            </select>
          </Field>
          <Field label="um mm:ss">
            <div className="row">
              <input type="number" min={0} max={59} value={atMin} onChange={(e) => set({ at_ms: e.target.value === "" ? null : Number(e.target.value) * 60000 + (Number(atSec) || 0) * 1000 })} style={{ width: 70 }} />:
              <input type="number" min={0} max={59} value={atSec} onChange={(e) => set({ at_ms: (Number(atMin) || 0) * 60000 + Number(e.target.value) * 1000 })} style={{ width: 70 }} />
            </div>
          </Field>
          {el.timing === "soft" && <><Field label="Sperrzeit davor (s)"><input type="number" min={0} max={900} value={el.lock_s ?? 60} onChange={(e) => set({ lock_s: Number(e.target.value) })} /></Field><Field label="Max. Überziehung (s)"><input type="number" min={0} max={900} value={el.overrun_s ?? 60} onChange={(e) => set({ overrun_s: Number(e.target.value) })} /></Field></>}
          {el.timing === "soft" && msField("max_wait_ms", "Max. Wartezeit (s)", "kommt das Element später, entfällt es")}
          {info.song && (
            <>
              <Field label="Energie-Ziel"><select value={el.energy_target ?? ""} onChange={(e) => set({ energy_target: e.target.value ? Number(e.target.value) : null })}><option value="">–</option>{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}</select></Field>
              <Field label="Stärke"><select value={el.energy_strength ?? 0} onChange={(e) => set({ energy_strength: Number(e.target.value) })}><option value={0}>aus</option><option value={1}>leicht</option><option value={2}>mittel</option><option value={3}>stark</option></select></Field>
              {msField("max_len_ms", "Max. Songlänge (s)")}
              <Field label="Auswahl"><select value={el.select_mode ?? "rotate"} onChange={(e) => set({ select_mode: e.target.value })}><option value="rotate">Rotieren</option><option value="newest">Neuester</option></select></Field>
              <Field label="Slot verlangt Genre"><input type="text" value={el.slot_rules?.genre ?? ""} onChange={(e) => set({ slot_rules: { ...el.slot_rules, genre: e.target.value || undefined } })} /></Field>
              <Field label="Slot verlangt Ära"><input type="text" value={el.slot_rules?.era ?? ""} onChange={(e) => set({ slot_rules: { ...el.slot_rules, era: e.target.value || undefined } })} /></Field>
            </>
          )}
          <div className="field full">
            <span>Nur senden während</span>
            <div className="row wrap">
              {DAYS.map((d, i) => (
                <label key={d} className="badge" style={{ cursor: "pointer", background: el.only_during?.days?.includes(i) ? "var(--accent-2)" : undefined }}>
                  <input type="checkbox" checked={!!el.only_during?.days?.includes(i)} onChange={() => {
                    const days: number[] = el.only_during?.days ?? [];
                    const next = days.includes(i) ? days.filter((x) => x !== i) : [...days, i];
                    set({ only_during: { ...(el.only_during ?? {}), days: next } });
                  }} /> {d}
                </label>
              ))}
              <input type="number" min={0} max={23} placeholder="ab h" value={el.only_during?.from ?? ""} onChange={(e) => set({ only_during: { ...(el.only_during ?? {}), from: e.target.value === "" ? undefined : Number(e.target.value) } })} style={{ width: 70 }} />
              <input type="number" min={1} max={24} placeholder="bis h" value={el.only_during?.to ?? ""} onChange={(e) => set({ only_during: { ...(el.only_during ?? {}), to: e.target.value === "" ? undefined : Number(e.target.value) } })} style={{ width: 70 }} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ClockEditor({ clock, onClose }: { clock: any | null; onClose: (changed: boolean) => void }) {
  const { data: cats } = useLoad<any[]>("/api/categories");
  const { data: groups } = useLoad<any[]>("/api/category-groups");
  const { data: pools } = useLoad<any[]>("/api/pools");
  const { data: news } = useLoad<any[]>("/api/news");
  const { data: weather } = useLoad<any[]>("/api/weather");
  const withKeys = (els: any[]): any[] => els.map((e) => ({ ...e, key: e.id ?? Math.random().toString(36).slice(2), children: withKeys(e.children ?? []) }));
  const [name, setName] = useState(clock?.name ?? "");
  const [color, setColor] = useState(clock?.color ?? "#2f6fb1");
  const [endLock, setEndLock] = useState(clock?.end_lock_s ?? 60);
  const [endOverrun, setEndOverrun] = useState(clock?.end_overrun_s ?? 60);
  const [hourEnd, setHourEnd] = useState(clock?.hour_end ?? "no_overrun");
  const [hourMode, setHourMode] = useState(clock?.hour_mode ?? "");
  const [stretch, setStretch] = useState(clock?.stretch ?? true);
  const [els, setEls] = useState<any[]>(withKeys(clock?.elements ?? []));
  const [sel, setSel] = useState<string | null>(null);
  const [advanced, setAdvanced] = useState(false);
  const [test, setTest] = useState<any | null>(null);
  const [drag, setDrag] = useState<string | null>(null);

  // Flat list with depth for display; blocks may contain elements (one level).
  const rows: { el: any; depth: number; parent: string | null }[] = [];
  for (const e of els) {
    rows.push({ el: e, depth: 0, parent: null });
    if (e.type === "block") for (const c of e.children ?? []) rows.push({ el: c, depth: 1, parent: e.key });
  }
  const find = (key: string | null) => rows.find((r) => r.el.key === key);
  const update = (key: string, patch: any) => setEls((cur) => cur.map((e) => (e.key === key ? { ...e, ...patch } : { ...e, children: (e.children ?? []).map((c: any) => (c.key === key ? { ...c, ...patch } : c)) })));
  const remove = (key: string) => setEls((cur) => cur.filter((e) => e.key !== key).map((e) => ({ ...e, children: (e.children ?? []).filter((c: any) => c.key !== key) })));
  const add = (type: string) => {
    const n = newElement(type);
    const target = find(sel);
    if (target && target.el.type === "block" && type !== "block") setEls((cur) => cur.map((e) => (e.key === target.el.key ? { ...e, children: [...(e.children ?? []), n] } : e)));
    else setEls((cur) => [...cur, n]);
    setSel(n.key);
  };
  const moveBefore = (dragKey: string, targetKey: string) => {
    if (dragKey === targetKey) return;
    const item = find(dragKey)?.el;
    const target = find(targetKey);
    if (!item || !target) return;
    let cur = els.filter((e) => e.key !== dragKey).map((e) => ({ ...e, children: (e.children ?? []).filter((c: any) => c.key !== dragKey) }));
    if (target.parent && item.type !== "block") {
      cur = cur.map((e) => (e.key === target.parent ? { ...e, children: (() => { const ch = [...e.children]; ch.splice(ch.findIndex((c: any) => c.key === targetKey), 0, item); return ch; })() } : e));
    } else {
      const i = cur.findIndex((e) => e.key === (target.parent ?? targetKey));
      cur.splice(i < 0 ? cur.length : i, 0, item);
    }
    setEls(cur);
  };
  const totalMs = flat(els).reduce((a, e) => a + (e.duration_ms ?? (typeInfo(e.type).song ? 210_000 : 60_000)), 0);
  const strip = (list: any[]): any[] => list.map(({ key, id, clock_id, parent_id, sort, category_name, category_color, group_name, media_title, pool_name, news_name, ...rest }) => ({ ...rest, children: strip(rest.children ?? []) }));
  const save = async (andTest = false) => {
    if (!name.trim()) return toast("Name fehlt", true);
    try {
      let id = clock?.id;
      const b = { name, color, end_lock_s: endLock, end_overrun_s: endOverrun, hour_end: hourEnd, hour_mode: hourMode, stretch, elements: strip(els) };
      if (id) await api(`/api/clocks/${id}`, { method: "PUT", body: b });
      else id = (await api("/api/clocks", { body: b })).id;
      toast("Uhr gespeichert – die kommenden Stunden werden neu geplant");
      if (andTest) setTest(await api(`/api/clocks/${id}/test`, { method: "POST" }));
      else onClose(true);
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  const selected = find(sel)?.el;
  return (
    <Modal wide title={clock ? `Uhr „${clock.name}“` : "Neue Uhr"} onClose={() => onClose(false)}>
      <div>
        <div className="row wrap" style={{ marginBottom: 10 }}>
          <input type="text" style={{ maxWidth: 240 }} value={name} onChange={(e) => setName(e.target.value)} placeholder="Name der Uhr" />
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ width: 44, height: 34 }} />
          <select style={{ width: 230 }} value={hourEnd} onChange={(e) => setHourEnd(e.target.value)} title="Was am Ende der Stunde passiert">
            <option value="no_overrun">Füllung: nicht überrollen</option><option value="repeat">Füllung: Musik wiederholen</option><option value="rolling">Füllung: rollend</option>
          </select>
          <select style={{ width: 200 }} value={hourMode} onChange={(e) => setHourMode(e.target.value)} title="Stundenende: fest = harter Start der nächsten Stunde zur vollen Stunde (Stunde wird aufgefüllt), weich = Titel spielt aus">
            <option value="">Stundenende: wie bisher</option><option value="fest">Stundenende: fest</option><option value="weich">Stundenende: weich</option>
          </select>
          <label>Sperrzeit vor Wechsel (s) <input type="number" min={0} max={900} value={endLock} onChange={(e) => setEndLock(Number(e.target.value))} /></label>
          <label>Max. Überziehung (s) <input type="number" min={0} max={900} value={endOverrun} onChange={(e) => setEndOverrun(Number(e.target.value))} /></label>
          <Switch checked={stretch} onChange={setStretch} label="Stretch & Squeeze" />
          <span className="spacer" />
          <Switch checked={advanced} onChange={setAdvanced} label="Erweitert" />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "170px minmax(0,1fr) 340px", gap: 12, alignItems: "start" }}>
          <div className="card" style={{ padding: 8 }}>
            <div className="small muted" style={{ marginBottom: 4 }}>Element hinzufügen</div>
            {ELEMENT_TYPES.filter((t) => !["program", "network", "relay"].includes(t.type)).filter((t) => advanced || ["music", "category_group", "title", "jingle", "sweeper", "ad_break", "news", "voicetrack", "block"].includes(t.type)).map((t) => (
              <button key={t.type} className="btn small" style={{ width: "100%", marginBottom: 3, justifyContent: "flex-start" }} onClick={() => add(t.type)}>
                <span style={{ color: t.color }}>{t.icon}</span> {t.label}
              </button>
            ))}
          </div>
          <div className="card" style={{ padding: 8 }}>
            <div className="row small muted" style={{ marginBottom: 6 }}>
              <span>Ablauf ({flat(els).length} Elemente)</span><span className="spacer" />
              <span className={Math.abs(totalMs - 3_600_000) > 300_000 ? "badge warn" : "badge ok"}>≈ {fmtMs(totalMs)} von 60:00</span>
            </div>
            {!rows.length && <Empty>Links Elemente hinzufügen. Musikkategorien und Jingles füllen sich automatisch.</Empty>}
            {rows.map(({ el, depth }) => (
              <div key={el.key} draggable onDragStart={() => setDrag(el.key)} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (drag) moveBefore(drag, el.key); setDrag(null); }}
                onClick={() => setSel(el.key)}
                style={{ marginLeft: depth * 18, padding: "5px 8px", marginBottom: 3, borderRadius: 6, borderLeft: `4px solid ${elColor(el)}`, background: sel === el.key ? "var(--accent-2)" : "#fbfaf6", cursor: "grab" }}>
                <div className="row" style={{ gap: 6 }}>
                  <span style={{ color: typeInfo(el.type).color }}>{typeInfo(el.type).icon}</span>
                  <span style={{ flex: 1 }}>{elLabel(el.category_id ? { ...el, category_name: cats?.find((c) => c.id === el.category_id)?.name } : el)}</span>
                  {el.at_ms != null && el.timing !== "none" && <span className="badge info">{el.timing === "hard" ? "hart" : el.timing === "soft" ? "weich" : "rückw."} {fmtMs(el.at_ms)}</span>}
                  {el.only_during && <span className="badge">nur zeitweise</span>}
                  <button className="iconbtn" onClick={(e) => { e.stopPropagation(); remove(el.key); }}>✕</button>
                </div>
              </div>
            ))}
          </div>
          <div className="card" style={{ padding: 10 }}>
            {selected ? <ElementProps el={selected} set={(p) => update(selected.key, p)} advanced={advanced} cats={cats ?? []} groups={groups ?? []} pools={pools ?? []} news={news ?? []} weather={weather ?? []} /> : <div className="muted small">Element anklicken, um es einzustellen. Ziehen ändert die Reihenfolge; ein Block nimmt Elemente auf, wenn er ausgewählt ist.</div>}
          </div>
        </div>
        {test && (
          <div className="card" style={{ marginTop: 12 }}>
            <h3>Testlauf (nächste Stunde, nichts gespeichert)</h3>
            <div className="small muted">{test.entries} Einträge · gefüllt {fmtMs(test.fill_ms)} · {test.unfilled} offen · {test.relaxed} mit gelockerten Regeln</div>
            <table className="list">
              <tbody>
                {(test.rows ?? []).map((r: any, i: number) => (
                  <tr key={i}>
                    <td className="small muted">{new Date(r.planned).toLocaleTimeString("de-DE", { minute: "2-digit", second: "2-digit" })}</td>
                    <td>{typeInfo(r.type).icon}</td>
                    <td>{r.title ? `${r.artist} – ${r.title}` : r.label}</td>
                    <td className="small">{r.category}</td>
                    <td className="small">{(r.badges ?? []).map((b: string) => <span key={b} className="badge warn" style={{ marginRight: 3 }}>{b}</span>)}</td>
                    <td className="small">{fmtMs(r.duration_ms)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="row" style={{ marginTop: 12 }}>
          <span className="spacer" />
          <button className="btn" onClick={() => onClose(!!test)}>Schließen</button>
          <button className="btn" onClick={() => save(true)}>Speichern & Testlauf</button>
          <button className="btn primary" onClick={() => save(false)}>Speichern</button>
        </div>
      </div>
    </Modal>
  );
}

export function Clocks() {
  const me = useMe();
  const canEdit = ["admin", "programm"].includes(me?.role);
  const { data: clocks, reload } = useLoad<any[]>("/api/clocks");
  const { data: cats } = useLoad<any[]>("/api/categories");
  const [edit, setEdit] = useState<any | null | undefined>(undefined);
  const [replace, setReplace] = useState(false);
  const [fromTargets, setFromTargets] = useState<any | null>(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  return (
    <div className="stack">
      <div className="row wrap">
        {canEdit && <button className="btn primary" onClick={() => setEdit(null)}>+ Uhr</button>}
        {canEdit && <button className="btn" onClick={() => setReplace(true)}>Alle Uhren bearbeiten …</button>}
        {canEdit && <button className="btn" onClick={() => setFromTargets({ name: "", songs_per_hour: 14, jingle_every: 2, jingle_category_id: "", top_of_hour: true })}>Uhr aus Ziel-Anteilen …</button>}
        {fromTargets && (
          <Modal title="Uhr aus Ziel-Anteilen bauen" onClose={() => setFromTargets(null)}>
            <p className="small muted" style={{ marginTop: 0 }}>Verteilt die Musikplätze nach den Ziel-Anteilen der Kategorien (Kategorien › Bearbeiten › Ziel-Anteil) und verschränkt sie gleichmäßig. Danach wie jede Uhr bearbeiten und mit „Testlauf“ prüfen.</p>
            <div className="form">
              <Field label="Name"><input type="text" value={fromTargets.name} onChange={(e) => setFromTargets({ ...fromTargets, name: e.target.value })} placeholder="z. B. Tagesuhr" /></Field>
              <Field label="Songs pro Stunde"><input type="number" min={4} max={24} value={fromTargets.songs_per_hour} onChange={(e) => setFromTargets({ ...fromTargets, songs_per_hour: Number(e.target.value) })} /></Field>
              <Field label="Jingle nach jedem … Song (0 = keiner)"><input type="number" min={0} max={8} value={fromTargets.jingle_every} onChange={(e) => setFromTargets({ ...fromTargets, jingle_every: Number(e.target.value) })} /></Field>
              <Field label="Jingle-Kategorie"><select value={fromTargets.jingle_category_id} onChange={(e) => setFromTargets({ ...fromTargets, jingle_category_id: e.target.value })}><option value="">automatisch (Jingle-Pools)</option>{(cats ?? []).filter((c) => c.kind !== "music").map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
            </div>
            <div className="row" style={{ marginTop: 12 }}><span className="spacer" />
              <button className="btn primary" onClick={async () => { try { const r = await api("/api/clocks/from-targets", { body: fromTargets }); toast(`Uhr angelegt: ${r.distribution.map((d: any) => `${d.category} ${d.songs}×`).join(", ")}`); setFromTargets(null); reload(); } catch (e: any) { toast(e.message, true); } }}>Bauen</button>
            </div>
          </Modal>
        )}
      </div>
      <Tip>Eine Uhr beschreibt eine Stunde. Im Wochenraster legst du fest, wann welche Uhr läuft; daraus entsteht der Sendeplan.</Tip>
      {!clocks?.length && <Empty>Noch keine Uhr. Lege die erste an – z. B. „Tagesuhr“ mit Jingle, Musik, Musik, Sweeper, Musik …</Empty>}
      <div style={{ display: "grid", gridAutoFlow: "column", gridAutoColumns: "minmax(220px, 1fr)", gap: 12, overflowX: "auto", alignItems: "start" }}>
        {(clocks ?? []).map((c) => (
          <div key={c.id} className="card" style={{ padding: 10, borderTop: `4px solid ${c.color}` }}>
            <div className="row"><b>{c.name}</b><span className="spacer" /><span className="small muted">{c.used ? "im Raster" : "nicht im Raster"}</span></div>
            <div style={{ display: "grid", placeItems: "center", margin: "8px 0" }}><Pie clock={c} /></div>
            <div className="small muted" style={{ marginBottom: 6 }}>≈ {fmtMs(c.estimate_ms)} · {c.hour_end === "repeat" ? "wiederholt" : c.hour_end === "rolling" ? "rollend" : "überrollt nicht"}</div>
            {flat(c.elements).map((e: any) => (
              <div key={e.id} className="small" style={{ borderLeft: `4px solid ${elColor(e)}`, padding: "2px 6px", marginBottom: 2, background: "#fbfaf6", borderRadius: 4 }}>
                {typeInfo(e.type).icon} {elLabel(e)}{e.at_ms != null && e.timing !== "none" ? ` · ${fmtMs(e.at_ms)}` : ""}
              </div>
            ))}
            {canEdit && (
              <div className="row" style={{ marginTop: 8 }}>
                <button className="btn small" onClick={() => setEdit(c)}>Bearbeiten</button>
                <button className="btn small" onClick={async () => { await api(`/api/clocks/${c.id}/duplicate`, { method: "POST" }); reload(); }}>Kopieren</button>
                <span className="spacer" />
                <button className="btn small danger" onClick={async () => { if (window.confirm(`Uhr „${c.name}“ löschen? Im Raster wird sie entfernt.`)) { await api(`/api/clocks/${c.id}`, { method: "DELETE" }); reload(); } }}>✕</button>
              </div>
            )}
          </div>
        ))}
      </div>
      {edit !== undefined && <ClockEditor clock={edit} onClose={(ch) => { setEdit(undefined); if (ch) reload(); }} />}
      {replace && (
        <Modal title="Alle Uhren bearbeiten" onClose={() => setReplace(false)}>
          <p className="muted small" style={{ marginTop: 0 }}>Ersetzt eine Kategorie in allen Uhren durch eine andere.</p>
          <div className="form">
            <Field label="Kategorie"><select value={from} onChange={(e) => setFrom(e.target.value)}><option value="">–</option>{(cats ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
            <Field label="ersetzen durch"><select value={to} onChange={(e) => setTo(e.target.value)}><option value="">–</option>{(cats ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
          </div>
          <div className="row" style={{ marginTop: 12 }}><span className="spacer" /><button className="btn primary" disabled={!from || !to} onClick={async () => { const r = await api("/api/clocks/replace-category", { body: { from, to } }); toast(`${r.count} Elemente geändert`); setReplace(false); reload(); }}>Ersetzen</button></div>
        </Modal>
      )}
    </div>
  );
}
